import { plainToClass } from "class-transformer";
import { DateTime } from "../util";
import { Contact } from "./Contact";
import type { Policy } from "./Policy";
import type { PolicyBilling } from "./PolicyBilling/PolicyBilling";

namespace Enums {
    export enum Action {
        UPCOMING_PAYMENT_REMINDER = "UPCOMING_PAYMENT_REMINDER",
        PAYMENT_DUE = "PAYMENT_DUE",
        PRE_CANCELLATION_NOTICE = "PRE_CANCELLATION_NOTICE",
        CANCELLATION_NOTICE = "CANCELLATION_NOTICE",
        PAYMENT_PROCESSED_NOTICE = "PAYMENT_PROCESSED_NOTICE",
        DOWN_PAYMENT_PROCESSED = "DOWN_PAYMENT_PROCESSED",
        ESCROW_PAYMENT_PROCESSED = "ESCROW_PAYMENT_PROCESSED",
    }
    export enum ActionFriendly {
        UPCOMING_PAYMENT_REMINDER = "Payment Reminder",
        PAYMENT_DUE = "Process ACH",
        PRE_CANCELLATION_NOTICE = "Pre-Cancellation Notice",
        CANCELLATION_NOTICE = "Cancellation Notice",
        PAYMENT_PROCESSED_NOTICE = "Payment Processed Notice",
        DOWN_PAYMENT_PROCESSED = "Down Payment Processed",
        ESCROW_PAYMENT_PROCESSED = "Escrow Payment Processed",
    }
}

const INVALID_POST_EXPIRATION_ACTIONS = [
    Enums.Action.UPCOMING_PAYMENT_REMINDER,
    Enums.Action.PRE_CANCELLATION_NOTICE,
    Enums.Action.CANCELLATION_NOTICE,
    Enums.Action.PAYMENT_PROCESSED_NOTICE,
    Enums.Action.DOWN_PAYMENT_PROCESSED,
    Enums.Action.ESCROW_PAYMENT_PROCESSED,
];

export class PolicyBillingNotification {
    static Action = Enums.Action;
    static ActionFriendly = Enums.ActionFriendly;

    static getActionDate(action: Enums.Action, dueDate: Date): DateTime {
        switch (action) {
            case Enums.Action.UPCOMING_PAYMENT_REMINDER:
                return DateTime.subtractDays(dueDate, 9);
            case Enums.Action.PRE_CANCELLATION_NOTICE:
                return DateTime.addDays(dueDate, 1);
            case Enums.Action.CANCELLATION_NOTICE:
                return DateTime.addDays(dueDate, 7);
            default:
                return new DateTime(dueDate);
        }
    }

    static getFriendlyActionName(action: Enums.Action): string {
        switch (action) {
            case Enums.Action.UPCOMING_PAYMENT_REMINDER:
                return "Payment Reminder";
            case Enums.Action.PAYMENT_DUE:
                return "Process ACH";
            case Enums.Action.PRE_CANCELLATION_NOTICE:
                return "Pre-Cancellation Notice";
            case Enums.Action.CANCELLATION_NOTICE:
                return "Cancellation Notice";
            case Enums.Action.PAYMENT_PROCESSED_NOTICE:
                return "Payment Processed Notice";
            case Enums.Action.DOWN_PAYMENT_PROCESSED:
                return "Down Payment Processed";
            case Enums.Action.ESCROW_PAYMENT_PROCESSED:
                return "Escrow Payment Processed";
            default:
                return "Unknown Action";
        }
    }

    static getActionIdFromFriendlyName(actionFriendly: Enums.ActionFriendly): Enums.Action | null {
        switch (actionFriendly) {
            case Enums.ActionFriendly.UPCOMING_PAYMENT_REMINDER:
                return Enums.Action.UPCOMING_PAYMENT_REMINDER;
            case Enums.ActionFriendly.PAYMENT_DUE:
                return Enums.Action.PAYMENT_DUE;
            case Enums.ActionFriendly.PRE_CANCELLATION_NOTICE:
                return Enums.Action.PRE_CANCELLATION_NOTICE;
            case Enums.ActionFriendly.CANCELLATION_NOTICE:
                return Enums.Action.CANCELLATION_NOTICE;
            case Enums.ActionFriendly.PAYMENT_PROCESSED_NOTICE:
                return Enums.Action.PAYMENT_PROCESSED_NOTICE;
            case Enums.ActionFriendly.DOWN_PAYMENT_PROCESSED:
                return Enums.Action.DOWN_PAYMENT_PROCESSED;
            case Enums.ActionFriendly.ESCROW_PAYMENT_PROCESSED:
                return Enums.Action.ESCROW_PAYMENT_PROCESSED;
            default:
                return null;
        }
    }

    static isValidNotification(
        policy: { expirationDate: Date },
        notification: Pick<PolicyBillingNotification, "action" | "dateScheduled">
    ): boolean {
        if (!notification.action) {
            return false;
        }

        if (
            new DateTime(notification.dateScheduled).isAfter(policy.expirationDate) &&
            INVALID_POST_EXPIRATION_ACTIONS.includes(notification.action)
        ) {
            return false;
        }

        return true;
    }

    static getNextNotification(
        billing: Pick<PolicyBilling, "billingPeriods"> & { policy: { id: string; expirationDate: Date } },
        nextNotificationScheduledDate = DateTime.today.addDays(1)
    ): PolicyBillingNotification | null {
        let bp = billing.billingPeriods[0];
        const notification = plainToClass(PolicyBillingNotification, {
            id: null,
            policyId: billing.policy.id,
            sendOnScheduledDate: true,
            skipped: null,
            dateCompleted: null,
            recipientId: null,
            recipient: null,
            stripePaymentIntentId: null,
            preCancellationLayerCreatedDate: null,

            action: null,
            dateScheduled: null,
            dateDue: null,
        });

        while (bp && !notification.action) {
            if (nextNotificationScheduledDate.isBefore(bp.paymentReminderDate, { excludeTime: true })) {
                const action = PolicyBillingNotification.Action.UPCOMING_PAYMENT_REMINDER;
                notification.action = action;
                notification.dateScheduled = bp.paymentReminderDate;
                notification.dateDue = bp.dueDate;
            } else if (nextNotificationScheduledDate.isBefore(bp.dueDate, { excludeTime: true })) {
                const action = PolicyBillingNotification.Action.PAYMENT_DUE;
                notification.action = action;
                notification.dateScheduled = bp.dueDate;
                notification.dateDue = bp.dueDate;
            }

            bp = bp.getNext();
        }
        if (!PolicyBillingNotification.isValidNotification(billing.policy, notification)) {
            return null;
        }

        return notification;
    }

    id: number;
    policyId: string;
    policy: Policy;
    dateScheduled: Date;
    dateDue: Date;
    action: Enums.Action;
    sendOnScheduledDate?: boolean;
    skipped?: boolean;
    dateCompleted?: Date;
    recipientId?: number;
    stripePaymentIntentId?: string;
    preCancellationLayerCreatedDate?: Date;
    recipient?: Contact;
    updatedByGuid?: string;
    updatedBy?: Contact;
    updatedDate?: Date;
    createdDate: Date;
}

export declare namespace PolicyBillingNotification {
    export type Action = Enums.Action;
    export type ActionFriendly = Enums.ActionFriendly;
}
